import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styles from '../Styles/Auth.module.css';
import logo from '../assets/GMF logo no background.png';
import { verifyToken } from '../Functions/verifyToken';
import { useAuth } from '../Components/AuthContext';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' });
    const [serverError, setServerError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const { login, token } = useAuth();
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;

    // Redirect logged-in users away from login page
    useEffect(() => {
        verifyToken(token).then((valid) => {
            if (valid) {
                navigate('/', { replace: true });
            }
        });
    }, [navigate]);

    // Basic client-side validation
    const validate = () => {
        const errors = { email: '', password: '' };
        let ok = true;

        if (!/\S+@\S+\.\S+/.test(email)) {
            errors.email = 'Please enter a valid email.';
            ok = false;
        }

        if (password.length < 8) {
            errors.password = 'Password must be at least 8 characters.';
            ok = false;
        }

        setFieldErrors(errors);
        return ok;
    };

    const attemptLogin = async (path) => {
        const res = await fetch(`${API_URL}${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
        });
        const payload = await res.json();
        if (!res.ok) {
            // throw an object containing both status & server detail
            const err = {
                status: res.status,
                message: payload.detail || payload.message || 'Login failed',
            };
            throw err;
        }
        return payload;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setServerError('');

        if (!validate()) return;
        setIsLoading(true);

        let saw403 = false;

        try {
            const data = await attemptLogin('/gmf-employee/login');
            return completeLogin(data, 'GMF_EMPLOYEE');
        } catch (err) {
            if (err.status === 403) {
                saw403 = true;
            }
        }

        try {
            const data = await attemptLogin('/institution-account/login');
            return completeLogin(data, 'INSTITUTION');
        } catch (err) {
            if (err.status === 403) {
                saw403 = true;
            }
        }

        if (saw403) {
            setServerError('Your account has not been approved yet.');
        } else {
            setServerError('Invalid email or password.');
        }

        setIsLoading(false);
    };

    const completeLogin = (data, role) => {
        login(data.token, role, data);

        // always go to home
        navigate('/', { replace: true });
    };

    return (
        <div className={styles.home}>
            <header className={styles.headerSection}>
                <div className={styles.contentContainer}>
                    {/* <img
                        src={logo}
                        alt="MD-Sensei Logo"
                        className={styles.logo}
                    /> */}
                    <h1 className={styles.title}>Login</h1>

                    <form onSubmit={handleSubmit} noValidate>
                        <div className={styles.formGroup}>
                            <label htmlFor="email">Email</label>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={
                                    fieldErrors.email ? styles.errorInput : ''
                                }
                                aria-describedby="email-error"
                            />
                            {fieldErrors.email && (
                                <span
                                    id="email-error"
                                    className={styles.errorMessage}
                                >
                                    {fieldErrors.email}
                                </span>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="password">Password</label>
                            <input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className={
                                    fieldErrors.password
                                        ? styles.errorInput
                                        : ''
                                }
                                aria-describedby="password-error"
                            />
                            {fieldErrors.password && (
                                <span
                                    id="password-error"
                                    className={styles.errorMessage}
                                >
                                    {fieldErrors.password}
                                </span>
                            )}
                        </div>

                        <button
                            type="submit"
                            className={styles.submitButton}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Logging in…' : 'Login'}
                        </button>

                        {serverError && (
                            <span className={styles.errorMessage}>
                                {serverError}
                            </span>
                        )}
                    </form>

                    <p className={styles.linkText}>
                        Forgot your password?{' '}
                        <Link to="/forgot-password">Reset it</Link>.
                    </p>
                    <p className={styles.linkText}>
                        Don't have an account?{' '}
                        <Link to="/create-account">Create one</Link>.
                    </p>
                </div>
            </header>
        </div>
    );
};

export default Login;
